import React, { useState, useMemo, useRef, useEffect } from "react";
import clsx from "clsx";
import Image from "next/image";

export interface Column<T> {
  id: keyof T | string;
  name: string | React.ReactNode;
  show?: boolean;
  render?: (rowData: T, rowIndex: number) => React.ReactNode;
  sort?: boolean;
  align?: "left" | "center" | "right";
  width?: number | string;
  collapseContent?: (rowData: T, rowIndex: number) => React.ReactNode;
}

export interface TableProps<T> {
  columns: Record<string, Column<T>>;
  data: T[];
  onRowClick?: (rowData: T, rowIndex: number) => void;
  customClassName?: string;
  tableCustomClassName?: string;
  loading?: boolean;
  loadingRowCount?: number;
  noDataText?: string;
  onSort?: (columnId: string) => void;
  sortField?: string;
  sortOrder?: "asc" | "desc";
  enableCollapse?: boolean;
}

const CollapseRow: React.FC<{
  isCollapsed: boolean;
  children: React.ReactNode;
  rowIndex: number;
  visibleColumnsLength: number;
}> = ({ isCollapsed, children, rowIndex, visibleColumnsLength }) => {
  const contentRef = useRef<HTMLDivElement>(null);
  const [height, setHeight] = useState<number>(0);

  useEffect(() => {
    if (contentRef.current) {
      setHeight(isCollapsed ? contentRef.current.scrollHeight : 0);
    }
  }, [isCollapsed]);

  return (
    <tr
      className={clsx(
        "transition-all duration-300 ease-in-out overflow-hidden",
        rowIndex % 2 === 0 ? "bg-transparent" : "bg-table-stripe/20"
      )}
    >
      <td colSpan={visibleColumnsLength + 1} className="px-6 overflow-hidden">
        <div
          ref={contentRef}
          className="transition-all duration-300 ease-in-out"
          style={{
            height: isCollapsed ? `${height}px` : "0px",
            opacity: isCollapsed ? 1 : 0,
          }}
        >
          <div className="py-4">{children}</div>
        </div>
      </td>
    </tr>
  );
};

const Table = <T extends object>({
  columns,
  data,
  onRowClick,
  customClassName,
  tableCustomClassName,
  loading = false,
  loadingRowCount = 5,
  noDataText = "No data available",
  onSort,
  sortField,
  sortOrder,
  enableCollapse = false,
}: TableProps<T>) => {
  const [collapsedRows, setCollapsedRows] = useState<Record<number, boolean>>(
    {}
  );
  const tableRef = useRef<HTMLDivElement>(null);

  const visibleColumns = useMemo(
    () => Object.values(columns).filter((col) => col.show !== false),
    [columns]
  );

  const hasCollapseContent = useMemo(
    () => visibleColumns.some((col) => col.collapseContent),
    [visibleColumns]
  );

  // Hoisted out of the row loop — it was being re-found for every row.
  const collapseColumn = useMemo(
    () => visibleColumns.find((col) => col.collapseContent),
    [visibleColumns]
  );

  /** Whether a row's whole width acts as the expand target. */
  const rowExpandable = enableCollapse && !!collapseColumn?.collapseContent;

  const handleSort = (columnId: string) => {
    onSort?.(columnId);
  };

  const toggleCollapse = (rowIndex: number) => {
    setCollapsedRows((prev) => ({
      ...prev,
      [rowIndex]: !prev[rowIndex],
    }));
  };

  const handleRowClick = (rowData: T, rowIndex: number) => {
    // The whole row expands, not just the +/- button: the button is a 24px
    // target at the far left of a table that scrolls sideways, so on a phone it
    // is often off-screen while the row the reader wants is right there.
    //
    // A click that ends a text selection is not a click on the row — without
    // this guard, copying a version string out of a cell would also toggle the
    // row underneath the selection.
    if (rowExpandable && !window.getSelection()?.toString()) {
      toggleCollapse(rowIndex);
    }
    onRowClick?.(rowData, rowIndex);
  };

  return (
    <div className={clsx("w-full overflow-hidden", customClassName)} ref={tableRef}>
      <div className="overflow-x-auto sa-hide-scrollbar">
        <table
          className={clsx(
            "min-w-full border-collapse table-fixed",
            tableCustomClassName
          )}
        >
          <colgroup>
            {enableCollapse && (
              <col style={{ width: 60 }} />
            )}
            {visibleColumns.map((column) => (
              <col key={String(column.id)} style={column.width ? { width: column.width } : undefined} />
            ))}
          </colgroup>
          <thead>
            <tr className="border-b border-text-default">
              {enableCollapse && (
                <th className="h-[56px] w-[60px] min-w-[60px] max-w-[60px] px-0"></th>
              )}
              {visibleColumns.map((column) => (
                <th
                  key={String(column.id)}
                  className={clsx(
                    // Tighter cell box + one type step down below `sm`: these
                    // tables scroll sideways by design, and the phone step trims
                    // roughly a fifth off the track the reader has to drag.
                    "h-[56px] px-4 py-3 sm:px-6 sm:py-4 font-semibold text-[14px] sm:text-[16px] leading-[24px] tracking-[0.5px] text-primary",
                    column.align === "center" && "text-center",
                    column.align === "right" && "text-right",
                    !column.align && "text-left",
                    column.sort && "cursor-pointer select-none hover:text-primary/80"
                  )}
                  onClick={() => column.sort && handleSort(String(column.id))}
                >
                  <div
                    className={clsx(
                      "flex items-center gap-2",
                      column.align === "center" && "justify-center",
                      column.align === "right" && "justify-end"
                    )}
                  >
                    {column.name}
                    {column.sort && (
                      <div className="flex flex-col gap-0.5">
                        <svg
                          width="8"
                          height="5"
                          viewBox="0 0 8 5"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                          className={clsx(
                            "transition-colors",
                            sortField === column.id && sortOrder === "asc"
                              ? "fill-primary"
                              : "fill-neutral-400"
                          )}
                        >
                          <path d="M4 0L7.4641 4.5H0.535898L4 0Z" />
                        </svg>
                        <svg
                          width="8"
                          height="5"
                          viewBox="0 0 8 5"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                          className={clsx(
                            "transition-colors",
                            sortField === column.id && sortOrder === "desc"
                              ? "fill-primary"
                              : "fill-neutral-400"
                          )}
                        >
                          <path d="M4 5L0.535898 0.5H7.4641L4 5Z" />
                        </svg>
                      </div>
                    )}
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {!loading ? (
              data.length > 0 ? (
                data.map((rowData, rowIndex) => {
                  const isCollapsed = collapsedRows[rowIndex];

                  return (
                    <React.Fragment key={rowIndex}>
                      <tr
                        className={clsx(
                          "relative group/row",
                          rowIndex % 2 === 0
                            ? "bg-transparent"
                            : "bg-table-stripe/20",
                          (onRowClick || rowExpandable) &&
                            "cursor-pointer hover:bg-table-stripe/40 hover:shadow-[0_2px_8px_rgba(236,25,56,0.1)]"
                        )}
                        onClick={() => handleRowClick(rowData, rowIndex)}
                      >
                        {enableCollapse && (
                          <td
                            className={clsx(
                              "w-[60px] min-w-[60px] max-w-[60px] px-0 py-3 sm:py-4 relative",
                              // Left accent line on row hover (moved from <tr> to first cell)
                              "before:absolute before:left-0 before:top-0 before:h-full before:w-[2px] before:bg-primary before:scale-y-0 before:transition-transform before:duration-300",
                              "group-hover/row:before:scale-y-100"
                            )}
                          >
                            <div className="flex items-center justify-center">
                              {rowExpandable ? (
                                <button
                                  type="button"
                                  aria-expanded={!!isCollapsed}
                                  // stopPropagation, or the row handler fires
                                  // straight after and toggles a second time —
                                  // the two would cancel out. It also stays the
                                  // keyboard path now that the row itself only
                                  // responds to a pointer.
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    toggleCollapse(rowIndex);
                                  }}
                                  className="relative flex cursor-pointer items-center justify-center w-6 h-6 transition-all duration-300 hover:scale-110 hover:rotate-90 group/btn"
                                >
                                  <Image
                                    src={
                                      isCollapsed
                                        ? "/images/Common/minus-sign.svg"
                                        : "/images/Common/plus-sign.svg"
                                    }
                                    alt={isCollapsed ? "Collapse" : "Expand"}
                                    width={24}
                                    height={24}
                                    className="w-6 h-6 relative z-10"
                                  />
                                  {/* Glow effect on hover */}
                                  <div className="absolute inset-0 rounded-full bg-primary/20 scale-0 group-hover/btn:scale-150 transition-transform duration-300 opacity-0 group-hover/btn:opacity-100" />
                                </button>
                              ) : (
                                <div className="w-6 h-6" />
                              )}
                            </div>
                          </td>
                        )}
                        {visibleColumns.map((column, colIndex) => (
                          <td
                            key={String(column.id)}
                            className={clsx(
                              // Same phone step as the header cells above.
                              "px-4 py-3 sm:px-6 sm:py-4 font-normal text-[14px] sm:text-[16px] leading-[24px] sm:leading-[28px] tracking-[0.5px] text-white",
                              column.align === "center" && "text-center",
                              column.align === "right" && "text-right",
                              !column.align && "text-left",
                              // Left accent line on row hover when there's no collapse column
                              !enableCollapse &&
                                hasCollapseContent === false &&
                                colIndex === 0 &&
                                "relative before:absolute before:left-0 before:top-0 before:h-full before:w-[2px] before:bg-primary before:scale-y-0 before:transition-transform before:duration-300 group-hover/row:before:scale-y-100"
                            )}
                          >
                            {typeof column.render === "function"
                              ? column.render(rowData, rowIndex)
                              : (rowData[
                                  column.id as keyof T
                                ] as React.ReactNode) ?? "-"}
                          </td>
                        ))}
                      </tr>

                      {enableCollapse && collapseColumn?.collapseContent && (
                        <CollapseRow
                          isCollapsed={!!isCollapsed}
                          rowIndex={rowIndex}
                          visibleColumnsLength={visibleColumns.length}
                        >
                          {collapseColumn.collapseContent(rowData, rowIndex)}
                        </CollapseRow>
                      )}
                    </React.Fragment>
                  );
                })
              ) : (
                <tr>
                  <td
                    colSpan={
                      visibleColumns.length +
                      (enableCollapse ? 1 : 0)
                    }
                    className="px-4 py-8 sm:px-6 sm:py-12 text-center text-neutral-400 text-[14px] sm:text-[16px]"
                  >
                    {noDataText}
                  </td>
                </tr>
              )
            ) : (
              Array.from({ length: loadingRowCount }).map((_, index) => (
                <tr
                  key={`skeleton-${index}`}
                  className={clsx(
                    index % 2 === 0 ? "bg-transparent" : "bg-table-stripe/20"
                  )}
                >
                  {enableCollapse && (
                    <td className="w-[60px] min-w-[60px] max-w-[60px] px-0 py-3 sm:py-4">
                      <div className="w-6 h-6 bg-neutral-700 rounded animate-pulse" />
                    </td>
                  )}
                  {visibleColumns.map((column) => (
                    <td key={String(column.id)} className="px-4 py-3 sm:px-6 sm:py-4">
                      <div className="h-[28px] bg-neutral-700 rounded animate-pulse" />
                    </td>
                  ))}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* CSS Animations */}
      <style jsx>{`
        /* Hide scrollbar but keep scroll (Windows/Chrome shows bars easily) */
        .sa-hide-scrollbar {
          scrollbar-width: none; /* Firefox */
          -ms-overflow-style: none; /* IE/Edge legacy */
        }
        .sa-hide-scrollbar::-webkit-scrollbar {
          width: 0;
          height: 0;
          display: none; /* Chrome/Safari */
        }

        /* Cyber grid pattern on row hover */
        .group\/row:hover {
          background-image: 
            linear-gradient(rgba(236, 25, 56, 0.02) 1px, transparent 1px),
            linear-gradient(90deg, rgba(236, 25, 56, 0.02) 1px, transparent 1px);
          background-size: 20px 20px;
        }
      `}</style>
    </div>
  );
};

export default Table;
